import { useState, useCallback } from 'react';
import { Check, Calculator, FlaskConical, Award, AlertTriangle } from 'lucide-react';
import { useExperiment } from '@/context';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';

// Educational content for each step
const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Calculating mg Vitamin C per Sample',
    content: 'The factor 8.806 mg/mL comes from the stoichiometry and normality of the iodine solution:\n\n1 equivalent of I₂ reacts with 1 equivalent of ascorbic acid (176.12 g/mol, 2 eq/mol = 88.06 g/eq)\n\nFor 0.10 N I₂: 88.06 × 0.10 = 8.806 mg ascorbic acid per mL I₂',
    whatHappens: 'Each sample volume of iodine is multiplied by the factor to give mg of ascorbic acid.',
    observe: [
      'mg Vit C = mL I₂ × 8.806',
      'Calculate for all 4 samples',
      'Values should be similar',
      'Units: milligrams per sample'
    ]
  },
  2: {
    title: 'Calculating mg per Tablet',
    content: 'Since we analyzed powder samples (not whole tablets), we need to scale up to find the vitamin C content per tablet. The ratio of average tablet mass to sample mass converts our result.',
    whatHappens: 'The mg vitamin C in each sample is multiplied by (avg tablet mass / sample mass) to get mg per tablet.',
    observe: [
      'mg/tablet = (mg in sample × avg tablet mass) / sample mass',
      'Uses the average tablet mass from Part 1',
      'Accounts for the fraction of tablet analyzed',
      'Should approach label claim (500 mg)'
    ]
  },
  3: {
    title: 'Calculating Average',
    content: 'The arithmetic mean of replicate measurements is the best estimate of the true value. With four replicates, we sum the values and divide by four.',
    whatHappens: 'All four "mg per tablet" values are averaged.',
    observe: [
      'Average = Sum of values / 4',
      'Check for outliers before averaging',
      'Average represents the reported result',
      'Compare to the label claim'
    ]
  },
  4: {
    title: 'Calculating Percent of Label',
    content: 'The percent of label claim indicates how well the product matches its advertised content. It is calculated as:\n\n% of Label = (Average mg found / Label claim) × 100',
    whatHappens: 'The average vitamin C content is compared to the label claim (500 mg).',
    observe: [
      '% = (avg mg / 500 mg) × 100',
      'Should be close to 100%',
      'Slight variation is normal',
      'Used for USP compliance'
    ]
  },
  5: {
    title: 'USP Compliance Evaluation',
    content: 'The United States Pharmacopeia (USP) sets standards for drug products. For vitamin C tablets, the product must contain 90-110% of the labeled amount. Values outside this range indicate the product does not meet quality standards.',
    whatHappens: 'The percent of label is compared to the USP acceptance criteria (90-110%).',
    observe: [
      'USP range: 90% to 110%',
      'PASS: within range',
      'FAIL: outside range',
      'Ensures product quality and safety'
    ]
  }
};

interface TrialResult {
  sample: number;
  volumeI2: number | null;
  mgVitC: number | null;
  perTablet: number | null;
  valid: boolean;
}

interface Sample {
  id: number;
  mass: number | null;
  prepared: boolean;
}

export default function Part3Calculations() {
  const { state, updatePartData, completeStep, isStepCompleted } = useExperiment();
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // Get data from previous parts
  const samplePrepData = state?.experimentData?.samplePrep || {
    avgTabletMass: 0.55,
    samples: [
      { id: 1, mass: 0.4012, prepared: true },
      { id: 2, mass: 0.3987, prepared: true },
      { id: 3, mass: 0.4021, prepared: true },
      { id: 4, mass: 0.3998, prepared: true }
    ]
  };

  const titrationData = state?.experimentData?.titration || {
    results: [
      { sample: 1, volumeI2: 25.5, mgVitC: null, perTablet: null, valid: true },
      { sample: 2, volumeI2: 25.2, mgVitC: null, perTablet: null, valid: true },
      { sample: 3, volumeI2: 25.8, mgVitC: null, perTablet: null, valid: true },
      { sample: 4, volumeI2: 25.4, mgVitC: null, perTablet: null, valid: true }
    ]
  };

  const partState = state?.experimentData?.calculations || {
    labelClaim: 500, // mg per tablet
    calculatedValues: [] as { sample: number; mgVitC: number; perTablet: number }[],
    averagePerTablet: null,
    percentOfLabel: null,
    uspCompliant: null
  };

  const [currentStep, setCurrentStep] = useState(1);

  const updateState = useCallback((updates: Record<string, unknown>) => {
    updatePartData('calculations', { ...partState, ...updates });
  }, [partState, updatePartData]);

  // Factor: 8.806 mg ascorbic acid per mL of 0.10 N iodine
  const FACTOR = 8.806;

  const handleCalculateMgPerSample = useCallback(() => {
    const calculatedValues = titrationData.results.map((result: TrialResult, i: number) => {
      const volumeI2 = result.volumeI2 || 0;
      const mgVitC = volumeI2 * FACTOR;
      return {
        sample: i + 1,
        mgVitC: parseFloat(mgVitC.toFixed(2)),
        perTablet: 0 // Will be calculated in next step
      };
    });

    updateState({ calculatedValues });
    completeStep('calculations-1');
    setCurrentStep(2);
  }, [titrationData.results, updateState, completeStep]);

  const handleCalculatePerTablet = useCallback(() => {
    const avgTabletMass = samplePrepData.avgTabletMass || 0.55;
    const samples = samplePrepData.samples;

    const updatedValues = partState.calculatedValues.map((cv: { sample: number; mgVitC: number; perTablet: number }, i: number) => {
      const sampleMass = samples[i]?.mass || 0.4;
      const perTablet = (cv.mgVitC * avgTabletMass) / sampleMass;
      return {
        ...cv,
        perTablet: parseFloat(perTablet.toFixed(2))
      };
    });

    updateState({ calculatedValues: updatedValues });
    completeStep('calculations-2');
    setCurrentStep(3);
  }, [samplePrepData, partState.calculatedValues, updateState, completeStep]);

  const handleCalculateAverage = useCallback(() => {
    const validValues = partState.calculatedValues.filter((cv: { perTablet: number }) => cv.perTablet > 0);
    const sum = validValues.reduce((acc: number, cv: { perTablet: number }) => acc + cv.perTablet, 0);
    const average = sum / validValues.length;

    updateState({ averagePerTablet: parseFloat(average.toFixed(2)) });
    completeStep('calculations-3');
    setCurrentStep(4);
  }, [partState.calculatedValues, updateState, completeStep]);

  const handleCalculatePercentLabel = useCallback(() => {
    if (partState.averagePerTablet) {
      const percent = (partState.averagePerTablet / partState.labelClaim) * 100;
      updateState({ percentOfLabel: parseFloat(percent.toFixed(1)) });
      completeStep('calculations-4');
      setCurrentStep(5);
    }
  }, [partState.averagePerTablet, partState.labelClaim, updateState, completeStep]);

  const handleEvaluateUSP = useCallback(() => {
    if (partState.percentOfLabel) {
      const isCompliant = partState.percentOfLabel >= 90 && partState.percentOfLabel <= 110;
      updateState({ uspCompliant: isCompliant });
      completeStep('calculations-5');
    }
  }, [partState.percentOfLabel, updateState, completeStep]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <Calculator size={16} />
          <h3>Part III: Calculations & USP Evaluation</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Calculate mg Vitamin C per Sample', hint: 'Use factor 8.806 mg/mL I2' },
            { id: 2, title: 'Calculate mg per Tablet', hint: 'Scale based on sample mass' },
            { id: 3, title: 'Calculate Average', hint: 'Mean of 4 replicate analyses' },
            { id: 4, title: 'Calculate Percent of Label', hint: 'Compare to 500 mg label claim' },
            { id: 5, title: 'Evaluate USP Compliance', hint: 'Must be 90-110% of label' }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${isStepCompleted(`calculations-${step.id}`) ? 'completed' : ''} ${
                currentStep === step.id ? 'active' : ''
              }`}
            >
              <div className={`step-number ${isStepCompleted(`calculations-${step.id}`) ? 'done' : ''}`}>
                {isStepCompleted(`calculations-${step.id}`) ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Data Summary */}
        <div className="data-summary">
          <h4>Experimental Data</h4>
          <div className="data-row">
            <span>Label Claim:</span>
            <span>{partState.labelClaim} mg</span>
          </div>
          <div className="data-row">
            <span>Avg Tablet Mass:</span>
            <span>{(samplePrepData.avgTabletMass || 0).toFixed(4)} g</span>
          </div>
          <div className="data-row">
            <span>I2 Factor:</span>
            <span>{FACTOR} mg/mL</span>
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Vitamin C Calculations"
        completedCount={state?.completedSteps.filter(s => s.startsWith('calculations-')).length || 0}
        totalSteps={5}
        equipment={[]}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="calculations-workspace">
          {/* Results Table */}
          <div className="results-table-container">
            <h3>Vitamin C Analysis Results</h3>
            <table className="results-table">
              <thead>
                <tr>
                  <th>Sample</th>
                  <th>Sample Mass (g)</th>
                  <th>Vol I2 (mL)</th>
                  <th>mg Vit C in Sample</th>
                  <th>mg per Tablet</th>
                </tr>
              </thead>
              <tbody>
                {titrationData.results.map((result: TrialResult, i: number) => {
                  const calcValue = partState.calculatedValues[i] || {};
                  const sampleMass = samplePrepData.samples[i]?.mass || 0;
                  return (
                    <tr key={i}>
                      <td>{result.sample}</td>
                      <td>{sampleMass.toFixed(4)}</td>
                      <td>{result.volumeI2?.toFixed(2) || '-'}</td>
                      <td>{calcValue.mgVitC?.toFixed(2) || '-'}</td>
                      <td>{calcValue.perTablet?.toFixed(2) || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Step-by-step calculations */}
          <div className="calculation-cards">
            {/* Step 1: mg Vitamin C */}
            <div className={`calc-card ${currentStep === 1 ? 'active' : ''} ${isStepCompleted('calculations-1') ? 'done' : ''}`}>
              <h4>Step 1: Calculate mg Vitamin C per Sample</h4>
              <div className="formula">
                mg Ascorbic Acid = mL I<sub>2</sub> × 8.806
              </div>
              {!isStepCompleted('calculations-1') && currentStep === 1 && (
                <button className="calc-btn" onClick={handleCalculateMgPerSample}>
                  <Calculator size={18} />
                  Calculate
                </button>
              )}
              {isStepCompleted('calculations-1') && (
                <div className="result-badge success">
                  <Check size={16} />
                  Calculated
                </div>
              )}
            </div>

            {/* Step 2: per Tablet */}
            <div className={`calc-card ${currentStep === 2 ? 'active' : ''} ${isStepCompleted('calculations-2') ? 'done' : ''}`}>
              <h4>Step 2: Calculate mg per Tablet</h4>
              <div className="formula">
                mg/tablet = (mg in sample × avg tablet mass) / sample mass
              </div>
              {!isStepCompleted('calculations-2') && currentStep === 2 && (
                <button className="calc-btn" onClick={handleCalculatePerTablet}>
                  <Calculator size={18} />
                  Calculate
                </button>
              )}
              {isStepCompleted('calculations-2') && (
                <div className="result-badge success">
                  <Check size={16} />
                  Calculated
                </div>
              )}
            </div>

            {/* Step 3: Average */}
            <div className={`calc-card ${currentStep === 3 ? 'active' : ''} ${isStepCompleted('calculations-3') ? 'done' : ''}`}>
              <h4>Step 3: Calculate Average</h4>
              {!isStepCompleted('calculations-3') && currentStep === 3 && (
                <button className="calc-btn" onClick={handleCalculateAverage}>
                  <Calculator size={18} />
                  Calculate Average
                </button>
              )}
              {partState.averagePerTablet && (
                <div className="result-display">
                  Average: <strong>{partState.averagePerTablet} mg/tablet</strong>
                </div>
              )}
            </div>

            {/* Step 4: Percent of Label */}
            <div className={`calc-card ${currentStep === 4 ? 'active' : ''} ${isStepCompleted('calculations-4') ? 'done' : ''}`}>
              <h4>Step 4: Calculate Percent of Label</h4>
              <div className="formula">
                % of Label = (avg mg/tablet ÷ {partState.labelClaim} mg) × 100
              </div>
              {!isStepCompleted('calculations-4') && currentStep === 4 && (
                <button className="calc-btn" onClick={handleCalculatePercentLabel}>
                  <Calculator size={18} />
                  Calculate
                </button>
              )}
              {partState.percentOfLabel && (
                <div className="result-display">
                  Percent of Label: <strong>{partState.percentOfLabel}%</strong>
                </div>
              )}
            </div>

            {/* Step 5: USP Evaluation */}
            <div className={`calc-card ${currentStep === 5 ? 'active' : ''} ${isStepCompleted('calculations-5') ? 'done' : ''}`}>
              <h4>Step 5: USP Compliance Evaluation</h4>
              <div className="usp-scale">
                <div className="usp-bar">
                  <div className="usp-zone fail-low">Fail (&lt;90%)</div>
                  <div className="usp-zone pass">Pass (90-110%)</div>
                  <div className="usp-zone fail-high">Fail (&gt;110%)</div>
                </div>
                {partState.percentOfLabel && (
                  <div
                    className="usp-marker"
                    style={{
                      left: `${Math.min(Math.max((partState.percentOfLabel - 80) / 0.4, 0), 100)}%`
                    }}
                  >
                    {partState.percentOfLabel}%
                  </div>
                )}
              </div>
              {!isStepCompleted('calculations-5') && currentStep === 5 && (
                <button className="calc-btn" onClick={handleEvaluateUSP}>
                  <Award size={18} />
                  Evaluate USP Compliance
                </button>
              )}
              {partState.uspCompliant !== null && (
                <div className={`usp-result ${partState.uspCompliant ? 'pass' : 'fail'}`}>
                  {partState.uspCompliant ? (
                    <>
                      <Check size={24} />
                      <div>
                        <h5>USP COMPLIANT</h5>
                        <p>Tablets contain {partState.percentOfLabel}% of labeled amount (90-110% required)</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={24} />
                      <div>
                        <h5>NOT USP COMPLIANT</h5>
                        <p>Tablets contain {partState.percentOfLabel}% of labeled amount (outside 90-110% range)</p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Completion Message */}
          {isStepCompleted('calculations-5') && (
            <div className="completion-banner">
              <FlaskConical size={32} />
              <div>
                <h3>Experiment Complete!</h3>
                <p>You have successfully determined the Vitamin C content in the tablets.</p>
              </div>
            </div>
          )}
        </div>
      </LabBench>

      <style>{`
        .calculations-workspace {
          display: flex;
          flex-direction: column;
          gap: 24px;
          padding: 20px;
          width: 100%;
          max-width: 700px;
        }

        .results-table-container {
          background: var(--bg-surface);
          border-radius: 12px;
          padding: 16px;
          border: 1px solid var(--border);
        }

        .results-table-container h3 {
          font-size: 14px;
          margin: 0 0 12px 0;
          color: var(--text-primary);
        }

        .results-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        .results-table th, .results-table td {
          padding: 8px 12px;
          text-align: center;
          border-bottom: 1px solid var(--border);
        }

        .results-table th {
          background: var(--bg-dark);
          color: var(--text-muted);
          font-weight: 600;
        }

        .calculation-cards {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .calc-card {
          background: var(--bg-surface);
          border: 2px solid var(--border);
          border-radius: 12px;
          padding: 16px;
          transition: all 0.3s;
        }

        .calc-card.active {
          border-color: var(--primary);
          background: rgba(255, 90, 54, 0.05);
        }

        .calc-card.done {
          border-color: var(--secondary);
          opacity: 0.8;
        }

        .calc-card h4 {
          font-size: 14px;
          margin: 0 0 12px 0;
          color: var(--text-primary);
        }

        .formula {
          background: var(--bg-dark);
          padding: 12px;
          border-radius: 6px;
          font-family: 'Times New Roman', serif;
          font-size: 14px;
          text-align: center;
          margin-bottom: 12px;
        }

        .calc-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 12px;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .calc-btn:hover {
          background: var(--primary-dark);
        }

        .result-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
        }

        .result-badge.success {
          background: rgba(0, 168, 107, 0.2);
          color: var(--secondary);
        }

        .result-display {
          padding: 12px;
          background: var(--bg-elevated);
          border-radius: 8px;
          text-align: center;
          font-size: 16px;
        }

        .usp-scale {
          position: relative;
          margin: 16px 0;
        }

        .usp-bar {
          display: flex;
          border-radius: 6px;
          overflow: hidden;
          height: 40px;
        }

        .usp-zone {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          color: white;
        }

        .usp-zone.fail-low, .usp-zone.fail-high {
          flex: 1;
          background: #EF4444;
        }

        .usp-zone.pass {
          flex: 2;
          background: #22C55E;
        }

        .usp-marker {
          position: absolute;
          top: -8px;
          transform: translateX(-50%);
          background: var(--text-primary);
          color: var(--bg-dark);
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 700;
        }

        .usp-marker::after {
          content: '';
          position: absolute;
          bottom: -6px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid var(--text-primary);
        }

        .usp-result {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          border-radius: 8px;
          margin-top: 12px;
        }

        .usp-result.pass {
          background: rgba(34, 197, 94, 0.2);
          color: #22C55E;
        }

        .usp-result.fail {
          background: rgba(239, 68, 68, 0.2);
          color: #EF4444;
        }

        .usp-result h5 {
          margin: 0 0 4px 0;
          font-size: 16px;
        }

        .usp-result p {
          margin: 0;
          font-size: 13px;
          opacity: 0.9;
        }

        .data-summary {
          margin-top: 20px;
          padding: 12px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .data-summary h4 {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 12px 0;
          text-transform: uppercase;
        }

        .data-row {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          padding: 4px 0;
          border-bottom: 1px solid var(--border);
        }

        .data-row:last-child {
          border-bottom: none;
        }

        .completion-banner {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
          border-radius: 12px;
          color: white;
        }

        .completion-banner h3 {
          margin: 0 0 4px 0;
          font-size: 18px;
        }

        .completion-banner p {
          margin: 0;
          opacity: 0.9;
          font-size: 14px;
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
      `}</style>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp6"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
